import { useCallback, useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import moment from 'moment-timezone';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  usePaginatedAvailableDates,
  useSessionBookedParticipations,
} from 'queries';
import { i18n } from 'i18n';
import { Theme } from '@app/theme';

import { Participation, Session } from '@app/api';
import { getDayText } from '@app/i18n';
import { CenteredButton, CenteredModal, useModalState } from '@app/core';
import { createDayMap } from '@app/form';

import { Dropdown } from 'components/Dropdown';
import { AppointmentListItem } from './components/AppointmentListItem';
import { EmptyAppointmentList } from './components/EmptyAppointmentList';
import { ParticipationDetailsModalPage } from './components/ParticipationDetailsModalPage';

const useStyles = createUseStyles((theme: Theme) => ({
  content: {
    padding: theme.marginBase * 6,
    overflow: 'hidden',
    [theme.media.tablet]: {
      paddingLeft: theme.marginBase * 3,
      paddingRight: theme.marginBase * 3,
    },
    [theme.media.mobile]: {
      paddingLeft: theme.marginBase,
      paddingRight: theme.marginBase,
    },
  },
  planningTitleLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.marginBase * 2,
    flexWrap: 'wrap',
  },
  planningTitle: {
    ...theme.fonts.h3,
    marginTop: theme.marginBase,
  },
  day: {
    ...theme.fonts.h5,
    marginTop: theme.marginBase * 3,
    marginBottom: theme.marginBase * 2,
    color: theme.colors.inkSecondary,
  },
  dropdownContainer: {
    marginTop: theme.marginBase,
    display: 'flex',
    marginRight: -theme.marginBase,
    '& > *': {
      marginRight: theme.marginBase,
    },
  },
  button: {
    marginTop: theme.marginBase * 2,
  },
}));

interface BookedFilter {
  value: string;
  label: string;
  sessionKey: 'appointments' | 'bookedAppointments' | 'availableAppointments';
}

interface Props {
  session: Session;
}

export const Planning = ({ session }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const bookedFilters = useMemo(
    (): BookedFilter[] => [
      {
        value: 'all',
        label: t('sessions.details.dropdownBooked.all'),
        sessionKey: 'appointments',
      },
      {
        value: 'booked',
        label: t('sessions.details.dropdownBooked.booked'),
        sessionKey: 'bookedAppointments',
      },
      {
        value: 'available',
        label: t('sessions.details.dropdownBooked.available'),
        sessionKey: 'availableAppointments',
      },
    ],
    [t],
  );

  const timeFilters = useMemo(
    () => [
      {
        value: 'future',
        label: t('sessions.details.dropdown.future'),
        filter: (date: Date | string) => moment(date).isAfter(moment()),
      },
      {
        value: 'past',
        label: t('sessions.details.dropdown.past'),
        filter: (date: Date | string) => moment(date).isBefore(moment()),
      },
      {
        value: 'all',
        label: t('sessions.details.dropdown.all'),
        filter: () => true,
      },
    ],
    [],
  );

  const [timeFilterObj, setTimeFilterObj] = useState(timeFilters[0]);
  const [bookedFilterObj, setBookedFilterObj] = useState(bookedFilters[0]);

  const {
    availableDates = [],
    hasNextPage,
    fetchNextPage,
  } = usePaginatedAvailableDates(session.taskSettingId);
  const { data: participations } = useSessionBookedParticipations(session.id);

  const [bookedDates, participationsByDate] = useMemo(() => {
    const dates: string[] = [];
    const participationsByDate: Record<string, Participation[]> = {};

    (participations || []).forEach((p: Participation) => {
      if (!p.appointment) {
        return;
      }
      dates.push(p.appointment.date);
      participationsByDate[p.appointment.date] = [
        ...(participationsByDate[p.appointment.date] || []),
        p,
      ];
    });
    return [dates, participationsByDate];
  }, [participations]);

  const allDates = useMemo(() => {
    return [...availableDates, ...bookedDates];
  }, [availableDates, bookedDates]);

  const openParticipantDetailsModal = useCallback(
    (participation?: Participation) => {
      if (!participation) {
        return;
      }
      navigate(`part/${participation.id}`);
    },
    [navigate],
  );

  const openCancelModal = useCallback(
    (id: string) => {
      navigate(`part/${id}/cancel`);
    },
    [navigate],
  );

  const [participantAddress, openAddressModal, closeAddressModal] =
    useModalState<string | undefined>(undefined);

  const onTimeFilterChange = (_: string, option: any) =>
    setTimeFilterObj(option);
  const onBookedFilterChange = (_: string, option: any) =>
    setBookedFilterObj(option);

  const dates =
    bookedFilterObj.value === 'all'
      ? allDates
      : bookedFilterObj.value === 'booked'
      ? bookedDates
      : availableDates;

  const dayHoursMap = createDayMap(dates, (a: Date | string) =>
    timeFilterObj.filter(a),
  );

  const dayRows = useMemo(() => {
    const seenByDate: Record<string, number> = {};

    return Object.entries(dayHoursMap).map(([day, slots]) => ({
      day,
      rows: slots.map((date: Date | string) => {
        if (typeof date !== 'string') {
          return { date, participation: undefined };
        }
        const index = seenByDate[date] || 0;
        seenByDate[date] = index + 1;
        return { date, participation: participationsByDate[date]?.[index] };
      }),
    }));
  }, [dayHoursMap, participationsByDate]);

  return (
    <div className={classes.content}>
      <div>
        <div className={classes.planningTitleLine}>
          <h2 className={classes.planningTitle}>
            <Trans i18nKey="sessions.details.planning" />
          </h2>
          <div className={classes.dropdownContainer}>
            <Dropdown
              options={bookedFilters}
              onChange={onBookedFilterChange}
              value={bookedFilterObj.value}
            />
            <Dropdown
              options={timeFilters}
              onChange={onTimeFilterChange}
              value={timeFilterObj.value}
            />
          </div>
        </div>

        {dayRows.map(({ day, rows }) => (
          <div key={day}>
            <h3 className={classes.day}>{getDayText(day, i18n.language)}</h3>

            <div>
              {rows.map(({ date, participation }) => (
                <AppointmentListItem
                  date={moment(date).toDate()}
                  session={session}
                  key={participation?.id || moment(date).format()}
                  onCancelRequest={openCancelModal}
                  onClick={openParticipantDetailsModal}
                  onAddressClick={openAddressModal}
                  participation={participation}
                />
              ))}
            </div>
          </div>
        ))}
        <ParticipationDetailsModalPage sessionId={session.id} />
        <CenteredModal
          isOpen={!!participantAddress}
          onRequestClose={closeAddressModal}
          title={t('sessions.details.participantAddress')}
        >
          <p>{participantAddress}</p>
        </CenteredModal>

        {!dayRows.length && (
          <EmptyAppointmentList
            filter={timeFilterObj.value}
            session={session}
          />
        )}
      </div>
      {hasNextPage && (
        <CenteredButton
          variant="muted"
          onClick={() => fetchNextPage()}
          className={classes.button}
          text={t('booking.appointment.showMoreAvailabilities')}
        />
      )}
    </div>
  );
};
